import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  alpha,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Fade,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import type {
  ChoiceGroupQuestion,
  FreeSelectionQuestion,
  GlobalSpecQuestion,
  InfoSlideQuestion,
  Question,
  TrimPercentageQuestion,
  TrimRankingQuestion,
} from '../../types';
import { EnumCutsheetQuestionnaireQuestionsType } from 'lib/graphql';
import { GlobalSpecWizard } from './global-spec-wizard';
import { FreeSelectionWizard } from './free-selection-wizard';
import { ChoiceGroupWizard } from './choice-group-wizard';
import { TrimRankingWizard } from './trim-ranking-wizard';
import { TrimPercentageWizard } from './trim-percentage-wizard';
import { InfoSlideWizard } from './info-slide-wizard';
import { AdvancedViewStep } from './advanced-view-step';
import { ReviewStep } from './review-step';
import type { QuestionnaireWizardProps } from './types';
import {
  QuestionnaireResponseProvider,
  useQuestionnaireResponse,
} from './questionnaire-response-context';

export type { QuestionnaireWizardProps };

// ─── Inner dialog component (reads all state from context) ────────────────────

interface QuestionnaireDialogInnerProps {
  open: boolean;
  printSettings?: QuestionnaireWizardProps['printSettings'];
}

const QuestionnaireDialogInner: FC<QuestionnaireDialogInnerProps> = ({
  open,
  printSettings,
}) => {
  const {
    questionnaire,
    catalog,
    currentStep,
    isFirstStep,
    isLastQuestionStep,
    isReviewStep,
    isSaveStep,
    heroExpanded,
    currentQuestion,
    answers,
    itemSpecValues,
    subSelections,
    handleNext,
    handleBack,
    handleGoToReview,
    handleGoToSave,
    handleNavigateToQuestion,
    handleClose,
    handleSave,
    isSaving,
    currentQuestionError,
  } = useQuestionnaireResponse();

  // Track slide direction: 'left' = forward, 'right' = backward
  const [slideDir, setSlideDir] = useState<'left' | 'right'>('left');
  // Keep a ref so the keydown handler always sees the latest value without re-registering
  const slideDirRef = useRef(slideDir);
  slideDirRef.current = slideDir;

  const onNext = useCallback(() => {
    setSlideDir('left');
    handleNext();
  }, [handleNext]);

  const onBack = useCallback(() => {
    setSlideDir('right');
    handleBack();
  }, [handleBack]);

  const onGoToReview = useCallback(() => {
    setSlideDir('left');
    handleGoToReview();
  }, [handleGoToReview]);

  const onGoToSave = useCallback(() => {
    setSlideDir('left');
    handleGoToSave();
  }, [handleGoToSave]);

  const onArrowRight = useCallback(() => {
    // Mirror footer button behavior for keyboard navigation.
    if (isSaveStep) return;
    if (isReviewStep) {
      onGoToSave();
      return;
    }
    if (currentQuestionError) return;
    if (isLastQuestionStep) {
      onGoToReview();
      return;
    }
    onNext();
  }, [
    isSaveStep,
    isReviewStep,
    onGoToSave,
    currentQuestionError,
    isLastQuestionStep,
    onGoToReview,
    onNext,
  ]);

  const onNavigateToQuestion = useCallback(
    (index: number) => {
      setSlideDir(index >= currentStep ? 'left' : 'right');
      handleNavigateToQuestion(index);
    },
    [handleNavigateToQuestion, currentStep],
  );

  // Arrow key navigation — skip when focus is inside a text/number input
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag)) return;
      if (e.key === 'ArrowRight') onArrowRight();
      if (e.key === 'ArrowLeft' && !isFirstStep) onBack();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onArrowRight, onBack, isFirstStep]);

  /** Step labels shown in the progress bar */
  const stepLabels = useMemo(
    () => [
      ...questionnaire.questions.map(
        (_q, _idx) =>
          `Question ${_idx + 1} of ${questionnaire.questions.length}`,
      ),
      'Review',
      'Save',
    ],
    [questionnaire.questions],
  );

  /**
   * Dispatch the correct question sub-component based on question type
   * @param question
   */
  const renderQuestionContent = (question: Question) => {
    switch (question.type) {
      case EnumCutsheetQuestionnaireQuestionsType.GlobalSpec:
        return <GlobalSpecWizard question={question as GlobalSpecQuestion} />;
      case EnumCutsheetQuestionnaireQuestionsType.FreeSelection:
        return (
          <FreeSelectionWizard question={question as FreeSelectionQuestion} />
        );
      case EnumCutsheetQuestionnaireQuestionsType.ChoiceGroup:
        return <ChoiceGroupWizard question={question as ChoiceGroupQuestion} />;
      case EnumCutsheetQuestionnaireQuestionsType.TrimRanking:
        return <TrimRankingWizard question={question as TrimRankingQuestion} />;
      case EnumCutsheetQuestionnaireQuestionsType.TrimPercentage:
        return (
          <TrimPercentageWizard question={question as TrimPercentageQuestion} />
        );
      case EnumCutsheetQuestionnaireQuestionsType.InfoSlide:
        return <InfoSlideWizard question={question as InfoSlideQuestion} />;
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen
      PaperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'background.paper',
          overflowY: 'hidden',
        },
      }}
    >
      {/* Header — title, description, progress */}
      <Box sx={{ px: 0, py: 0, backgroundColor: '#FFF' }}>
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          sx={{
            maxWidth: 'md',
            mx: 'auto',
            width: '100%',
            px: { xs: 3, md: 1 },
            pt: 1,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h4"
              sx={{ color: 'primary.dark', fontWeight: 600 }}
            >
              {isReviewStep
                ? 'Review Cutsheet'
                : isSaveStep
                  ? 'Save Details'
                  : (currentQuestion?.title ?? '')}
            </Typography>
            {!isReviewStep && !isSaveStep && (
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mt: 0.5 }}
              >
                {`Step ${currentStep + 1} of ${questionnaire.questions.length}`}
              </Typography>
            )}
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{ color: 'text.secondary', ml: 1 }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
        <Box
          sx={{
            maxWidth: 'md',
            mx: 'auto',
            width: '100%',
            px: { xs: 3, md: 1 },
            pb: 1,
          }}
        >
          <LinearProgress
            variant="determinate"
            value={
              stepLabels.length > 1
                ? ((currentStep + 1) / stepLabels.length) * 100
                : 100
            }
            sx={{ mt: 1.5, borderRadius: 1, height: 10 }}
          />
        </Box>
      </Box>

      {/* ── Body ─────────────────────────────────────────────────────────── */}
      <Fade in={!heroExpanded} unmountOnExit>
        <DialogContent
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: 3,
            py: 2,
            backgroundColor: (theme) =>
              isReviewStep || isSaveStep
                ? 'transparent'
                : alpha(theme.palette.primary.light, 0.05),
            // Fade out the first/last 32px to hint at scrollable content above/below
            maskImage: (theme) =>
              `linear-gradient(to bottom, ${alpha(theme.palette.primary.light, 0.5)} 0%, ${theme.palette.primary.light} 32px, ${theme.palette.primary.light} calc(100% - 32px),  ${alpha(theme.palette.primary.light, 0.5)} 100%)`,
            WebkitMaskImage: (theme) =>
              `linear-gradient(to bottom,  ${alpha(theme.palette.primary.light, 0.5)} 0%, ${theme.palette.primary.light} 32px, ${theme.palette.primary.light} calc(100% - 32px),  ${alpha(theme.palette.primary.light, 0.5)} 100%)`,
          }}
        >
          <Box
            sx={{ maxWidth: 'md', mx: 'auto', width: '100%', height: '100%' }}
          >
            <Box
              key={currentStep}
              sx={{
                height: '100%',
                animation:
                  slideDir === 'left'
                    ? 'slideInFromRight 0.25s ease'
                    : 'slideInFromLeft 0.25s ease',
                '@keyframes slideInFromRight': {
                  from: { transform: 'translateX(32px)', opacity: 0 },
                  to: { transform: 'translateX(0)', opacity: 1 },
                },
                '@keyframes slideInFromLeft': {
                  from: { transform: 'translateX(-32px)', opacity: 0 },
                  to: { transform: 'translateX(0)', opacity: 1 },
                },
              }}
            >
              {isSaveStep && (
                <ReviewStep
                  questionnaire={questionnaire}
                  catalog={catalog}
                  answers={answers}
                  itemSpecValues={itemSpecValues}
                  subSelections={subSelections}
                  onNavigateToQuestion={onNavigateToQuestion}
                />
              )}

              {isReviewStep && (
                <AdvancedViewStep printSettings={printSettings} />
              )}

              {!!currentQuestion && (
                <Stack spacing={2}>
                  {currentQuestion.description && (
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      whiteSpace="pre-wrap"
                    >
                      {currentQuestion.description}
                    </Typography>
                  )}
                  {renderQuestionContent(currentQuestion)}
                </Stack>
              )}
            </Box>
          </Box>
        </DialogContent>
      </Fade>

      {/* ── Validation message ─────────────────────────────────────────── */}
      {!!currentQuestionError && (
        <Alert
          color="error"
          sx={{ px: 3, pb: 0.5, textAlign: 'center', borderRadius: 0 }}
        >
          {currentQuestionError}
        </Alert>
      )}

      {/* ── Navigation ───────────────────────────────────────────────────── */}
      <Fade in={!heroExpanded} unmountOnExit>
        <DialogActions
          sx={{
            px: 2,
            justifyContent: 'space-between',
            borderTopWidth: 1,
            borderColor: 'divider',
            borderStyle: 'solid',
          }}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            disabled={isFirstStep}
            variant="outlined"
          >
            Back
          </Button>

          <Stack direction="row" spacing={1}>
            {isFirstStep && (
              <Button variant="text" onClick={onGoToReview}>
                Advanced View
              </Button>
            )}

            {isSaveStep ? (
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? 'Saving…' : 'Done'}
              </Button>
            ) : isReviewStep ? (
              <Button
                endIcon={<ArrowForwardIcon />}
                onClick={onGoToSave}
                variant="contained"
              >
                Save
              </Button>
            ) : (
              <Button
                endIcon={isLastQuestionStep ? undefined : <ArrowForwardIcon />}
                onClick={isLastQuestionStep ? onGoToReview : onNext}
                variant="contained"
                disabled={!!currentQuestionError}
              >
                {isLastQuestionStep ? 'Review' : 'Next'}
              </Button>
            )}
          </Stack>
        </DialogActions>
      </Fade>
    </Dialog>
  );
};

// ─── Public component (mounts provider + inner dialog) ───────────────────────

/**
 * Full questionnaire preview dialog.
 *
 * The `QuestionnaireResponseProvider` wraps the dialog so that every
 * sub-component can read and update state via `useQuestionnaireResponse()`
 * without any prop drilling.
 * @param root0
 * @param root0.open
 * @param root0.onClose
 * @param root0.onSave
 * @param root0.questionnaire
 * @param root0.catalog
 * @param root0.printSettings
 * @param root0.isProcessor
 * @param root0.producerId
 * @param root0.buyerId
 * @param root0.cutsheetType
 */
export const QuestionnaireWizard: FC<QuestionnaireWizardProps> = ({
  open,
  onClose,
  onSave,
  questionnaire,
  catalog,
  printSettings,
  isProcessor,
  producerId,
  buyerId,
  cutsheetType,
  initialResponse,
}) => (
  <QuestionnaireResponseProvider
    questionnaire={questionnaire}
    catalog={catalog}
    onClose={onClose}
    onSave={onSave}
    isProcessor={isProcessor}
    producerId={producerId}
    buyerId={buyerId}
    fixedCutsheetType={cutsheetType}
    initialResponse={initialResponse}
  >
    <QuestionnaireDialogInner open={open} printSettings={printSettings} />
  </QuestionnaireResponseProvider>
);
